// src/pages/ClientsPage.tsx
import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Typography, message, Space, Tooltip } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import { mockClients } from '../data/mockData';
import { Client } from '../types';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>(mockClients);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const columns: ColumnsType<Client> = [
    {
      title: 'Razón Social',
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: 'Persona de Contacto',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
    },
    {
      title: 'Email',
      dataIndex: 'contactEmail',
      key: 'contactEmail',
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 120,
      render: (_: any, record: Client) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewClient(record)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditClient(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleAddClient = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = (values: Omit<Client, 'id'>) => {
    const newClient: Client = {
      id: `c${clients.length + 1}`,
      ...values,
    };
    
    setClients([...clients, newClient]);
    message.success('Cliente agregado exitosamente');
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleViewClient = (client: Client) => {
    setSelectedClient(client);
    setIsViewModalOpen(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    editForm.setFieldsValue(client);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = (values: Omit<Client, 'id'>) => {
    if (!selectedClient) return;
    
    const updatedClients = clients.map(c => 
      c.id === selectedClient.id ? { ...c, ...values } : c
    );
    
    setClients(updatedClients);
    message.success('Cliente actualizado exitosamente');
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedClient(null);
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedClient(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>Gestión de Clientes</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddClient}>
          Agregar Cliente
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={clients}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="Agregar Nuevo Cliente"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Razón Social"
            name="companyName"
            rules={[{ required: true, message: 'Por favor ingrese la razón social' }]}
          >
            <Input placeholder="Ej: Acme Corp" />
          </Form.Item>

          <Form.Item
            label="Persona de Contacto"
            name="contactPerson"
            rules={[{ required: true, message: 'Por favor ingrese el nombre del contacto' }]}
          >
            <Input placeholder="Ej: Juan Pérez" />
          </Form.Item>

          <Form.Item
            label="Email de Contacto"
            name="contactEmail"
            rules={[
              { required: true, message: 'Por favor ingrese el email' },
              { type: 'email', message: 'Ingrese un email válido' }
            ]}
          >
            <Input placeholder="contacto@empresa.com" />
          </Form.Item>

          <Form.Item label="NIT/RUT" name="nit">
            <Input placeholder="900123456-7" />
          </Form.Item>

          <Form.Item label="Sector" name="sector">
            <Select placeholder="Seleccione el sector">
              <Select.Option value="Tecnología">Tecnología</Select.Option>
              <Select.Option value="Manufactura">Manufactura</Select.Option>
              <Select.Option value="Servicios">Servicios</Select.Option>
              <Select.Option value="Comercio">Comercio</Select.Option>
              <Select.Option value="Salud">Salud</Select.Option>
              <Select.Option value="Educación">Educación</Select.Option>
              <Select.Option value="Otro">Otro</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Dirección" name="address">
            <Input.TextArea placeholder="Dirección completa" rows={2} />
          </Form.Item>

          <Form.Item label="Teléfono" name="phone">
            <Input placeholder="+57 310 1234567" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Crear Cliente
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Edición */}
      <Modal
        title="Editar Cliente"
        open={isEditModalOpen}
        onCancel={handleCancelEdit}
        footer={null}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            label="Razón Social"
            name="companyName"
            rules={[{ required: true, message: 'Por favor ingrese la razón social' }]}
          >
            <Input placeholder="Ej: Acme Corp" />
          </Form.Item>

          <Form.Item
            label="Persona de Contacto"
            name="contactPerson"
            rules={[{ required: true, message: 'Por favor ingrese el nombre del contacto' }]}
          >
            <Input placeholder="Ej: Juan Pérez" />
          </Form.Item>

          <Form.Item
            label="Email de Contacto"
            name="contactEmail"
            rules={[
              { required: true, message: 'Por favor ingrese el email' },
              { type: 'email', message: 'Ingrese un email válido' }
            ]}
          >
            <Input placeholder="contacto@empresa.com" />
          </Form.Item>

          <Form.Item label="NIT/RUT" name="nit">
            <Input placeholder="900123456-7" />
          </Form.Item>

          <Form.Item label="Sector" name="sector">
            <Select placeholder="Seleccione el sector">
              <Select.Option value="Tecnología">Tecnología</Select.Option>
              <Select.Option value="Manufactura">Manufactura</Select.Option>
              <Select.Option value="Servicios">Servicios</Select.Option>
              <Select.Option value="Comercio">Comercio</Select.Option>
              <Select.Option value="Salud">Salud</Select.Option>
              <Select.Option value="Educación">Educación</Select.Option>
              <Select.Option value="Otro">Otro</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Dirección" name="address">
            <Input.TextArea placeholder="Dirección completa" rows={2} />
          </Form.Item>

          <Form.Item label="Teléfono" name="phone">
            <Input placeholder="+57 310 1234567" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancelEdit}>Cancelar</Button>
              <Button type="primary" htmlType="submit">Guardar Cambios</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Detalles */}
      <Modal
        title="Detalles del Cliente"
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalOpen(false)}>
            Cerrar
          </Button>
        ]}
      >
        {selectedClient && (
          <div>
            <p><strong>Razón Social:</strong> {selectedClient.companyName}</p>
            <p><strong>NIT/RUT:</strong> {selectedClient.nit || 'No especificado'}</p>
            <p><strong>Sector:</strong> {selectedClient.sector || 'No especificado'}</p>
            <p><strong>Dirección:</strong> {selectedClient.address || 'No especificada'}</p>
            <p><strong>Persona de Contacto:</strong> {selectedClient.contactPerson}</p>
            <p><strong>Email:</strong> {selectedClient.contactEmail}</p>
            <p><strong>Teléfono:</strong> {selectedClient.phone || 'No especificado'}</p>
            <p><strong>ID:</strong> {selectedClient.id}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ClientsPage;
